import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    ModalSubHeading,
    ModalHeading,
    ModalHeader,
    MobileIcon,
} from "../Styles/InputStyles";
import { MdClose, MdArrowBack } from "react-icons/md";
import tw from "tailwind-styled-components";
import * as Yup from "yup";

// Step 1 Schema
const step1Schema = Yup.object().shape({
    fullName: Yup.string()
        .matches(/^[A-Za-z0-9. ]+$/, "Only letters, numbers, spaces & dots are allowed.")
        .min(4, "Name must be at least 4 characters long")
        .required("Partner name is required"),

    mobile: Yup.string()
        .matches(/^\d{10}$/, "Mobile number must be 10 digits")
        .required("Mobile number is required"),

    email: Yup.string()
        .email("Invalid email format")
        .nullable(),

    ownershipPercentage: Yup.number()
        .typeError("Ownership share must be a number")
        .min(1, "Minimum 1% required")
        .max(99, "Cannot be more than 99%")
        .required("Ownership share is required"),
});

// Step 2 Schema
const step2Schema = Yup.object().shape({
    firmName: Yup.string()
        .matches(/^[A-Za-z0-9. ]+$/, "Only letters, numbers, spaces & dots are allowed.")
        .min(4, "Firm name must be at least 4 characters long")
        .required("Firm name is required"),

    // firmEmail: Yup.string()
    //     .email("Invalid email format")
    //     .required("Firm email is required"),

    firmPan: Yup.string()
        .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN number")
        .required("Firm PAN is required"),

    firmGst: Yup.string()
        .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, "Invalid GST number")
        .nullable(),
});

const Add = ({ addPartnerMutate, addPartnerLoading, handleClose }) => {
    const [height, setHeight] = useState(window.innerHeight);
    const [currentStep, setCurrentStep] = useState(1);
    const [step1Data, setStep1Data] = useState(null);

    const initialValuesStep1 = {
        fullName: "",
        mobile: "",
        email: "",
        ownershipPercentage: 0
    }

    const initialValuesStep2 = {
        firmName: "",
        // firmEmail: "",
        firmPan: "",
        firmGst: ""
    }

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleStep1Submit = (values) => {
        setStep1Data(values);
        setCurrentStep(2);
    };

    const handleStep2Submit = (values) => {
        const finalData = {
            ...step1Data,
            ...values
        };

        addPartnerMutate({
            name: finalData.fullName,
            mobile: finalData.mobile,
            email: finalData.email,
            ownershipPercentage: finalData.ownershipPercentage,
            firmName: finalData.firmName,
            // firmEmail: finalData.firmEmail,
            pan: finalData.firmPan,
            gstNo: finalData.firmGst
        });
    };

    const handleBackToStep1 = () => {
        setCurrentStep(1);
    };

    const handleCancel = () => {
        if (currentStep === 2) {
            setCurrentStep(1);
            setStep1Data(null);
        } else {
            handleClose();
        }
    };
    const handlePanChange = (e, setFieldValue) => {
        const value = e.target.value.toUpperCase();
        setFieldValue("firmPan", value);
    };

    const handleGstChange = (e, setFieldValue) => {
        const value = e.target.value.toUpperCase();
        setFieldValue("firmGst", value);
    };
    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">
                    Add Partner
                </h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 h-full flex flex-col justify-between">
                {currentStep === 1 ? (
                    <Formik
                        initialValues={initialValuesStep1}
                        onSubmit={handleStep1Submit}
                        validationSchema={step1Schema}
                    >
                        {({ values, setFieldValue, errors, touched }) => (
                            <Form>
                                <ModalSubHeading>Step 1</ModalSubHeading>
                                <ModalHeading>Personal Details</ModalHeading>
                                <ScrollableContent className="max-h-[calc(100dvh-180px)]">
                                    <Wrapper>
                                        <InputGroup>
                                            <Label htmlFor="fullName">Name *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="fullName"
                                                    id="fullName"
                                                    autoComplete="off"
                                                    placeholder="Enter partner name"
                                                />
                                            </FieldWrappers>
                                            {errors.fullName && touched.fullName && (
                                                <ErrorMessage>{errors.fullName}</ErrorMessage>
                                            )}
                                        </InputGroup>

                                        <InputGroup>
                                            <Label htmlFor="mobile">Mobile Number *</Label>
                                            <FieldWrappers>
                                                <MobileIcon />
                                                <Field
                                                    type="text"
                                                    name="mobile"
                                                    id="mobile"
                                                    autoComplete="off"
                                                    maxLength={10}
                                                    className="pl-5"
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (/^\d{0,10}$/.test(value)) {
                                                            setFieldValue("mobile", value);
                                                        }
                                                    }}
                                                    placeholder="Enter 10-digit mobile number"
                                                />
                                            </FieldWrappers>
                                            {errors.mobile && touched.mobile && (
                                                <ErrorMessage>{errors.mobile}</ErrorMessage>
                                            )}
                                        </InputGroup>

                                        <InputGroup>
                                            <Label htmlFor="email">Partner Email</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="email"
                                                    name="email"
                                                    id="email"
                                                    autoComplete="off"
                                                    placeholder="Enter email address (optional)"
                                                />
                                            </FieldWrappers>
                                            {errors.email && touched.email && (
                                                <ErrorMessage>{errors.email}</ErrorMessage>
                                            )}
                                        </InputGroup>

                                        <InputGroup>
                                            <Label htmlFor="ownershipPercentage">Ownership % Share *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="number"
                                                    name="ownershipPercentage"
                                                    id="ownershipPercentage"
                                                    min="1"
                                                    max="99"
                                                    placeholder="Enter ownership percentage"
                                                    className="w-full pl-2"
                                                    value={values.ownershipPercentage || ""}
                                                    onChange={(e) => {
                                                        setFieldValue("ownershipPercentage", e.target.value);
                                                    }}
                                                />
                                            </FieldWrappers>
                                            {errors.ownershipPercentage && touched.ownershipPercentage && (
                                                <ErrorMessage>{errors.ownershipPercentage}</ErrorMessage>
                                            )}
                                        </InputGroup>
                                    </Wrapper>
                                </ScrollableContent>
                                <ActionSection>
                                    <SideCancelBtn type="button" onClick={handleCancel}>
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn type="submit">
                                        Next
                                    </SubmitBtn>
                                </ActionSection>
                            </Form>
                        )}
                    </Formik>
                ) : (
                    <Formik
                        initialValues={initialValuesStep2}
                        onSubmit={handleStep2Submit}
                        validationSchema={step2Schema}
                    >
                        {({ values, setFieldValue, errors, touched }) => (
                            <Form>
                                <ModalSubHeading>Step 2</ModalSubHeading>
                                <ModalHeading>Firm Details</ModalHeading>
                                <ScrollableContent className="max-h-[calc(100dvh-180px)]">
                                    <Wrapper>
                                        <div className="space-y-4">
                                            <InputGroup>
                                                <Label htmlFor="firmName">Firm Name *</Label>
                                                <FieldWrappers>
                                                    <Field
                                                        type="text"
                                                        name="firmName"
                                                        id="firmName"
                                                        autoComplete="off"
                                                        placeholder="Enter firm name"
                                                    />
                                                </FieldWrappers>
                                                {errors.firmName && touched.firmName && (
                                                    <ErrorMessage>{errors.firmName}</ErrorMessage>
                                                )}
                                            </InputGroup>

                                            {/* <InputGroup>
                                                <Label htmlFor="firmEmail">Firm Email *</Label>
                                                <FieldWrappers>
                                                    <Field
                                                        type="email"
                                                        name="firmEmail"
                                                        id="firmEmail"
                                                        autoComplete="off"
                                                        placeholder="Enter firm email address"
                                                    />
                                                </FieldWrappers>
                                                {errors.firmEmail && touched.firmEmail && (
                                                    <ErrorMessage>{errors.firmEmail}</ErrorMessage>
                                                )}
                                            </InputGroup> */}

                                            <InputGroup>
                                                <Label htmlFor="firmPan">Firm PAN *</Label>
                                                <FieldWrappers>
                                                    <Field
                                                        type="text"
                                                        name="firmPan"
                                                        id="firmPan"
                                                        autoComplete="off"
                                                        maxLength={10}
                                                        placeholder="Enter firm PAN number"
                                                        value={values.firmPan || ''}
                                                        onChange={(e) => handlePanChange(e, setFieldValue)}
                                                    // onBlur={() => {
                                                    //     if (values.firmPan) {
                                                    //         setFieldValue("firmPan", values.firmPan.toUpperCase());
                                                    //     }
                                                    // }}
                                                    // onBlur={() => setFieldValue("firmPan", values.firmPan.toUpperCase())}
                                                    //style={{ textTransform: 'uppercase' }}
                                                    />
                                                </FieldWrappers>
                                                {errors.firmPan && touched.firmPan && (
                                                    <ErrorMessage>{errors.firmPan}</ErrorMessage>
                                                )}
                                            </InputGroup>

                                            <InputGroup>
                                                <Label htmlFor="firmGst">Firm GST</Label>
                                                <FieldWrappers>
                                                    <Field
                                                        type="text"
                                                        name="firmGst"
                                                        id="firmGst"
                                                        autoComplete="off"
                                                        placeholder="Enter GST number (optional)"
                                                        value={values.firmGst || ''}
                                                        onChange={(e) => handleGstChange(e, setFieldValue)}
                                                    // onBlur={() => {
                                                    //     if (values.firmGst) {
                                                    //         setFieldValue("firmGst", values.firmGst.toUpperCase());
                                                    //     }
                                                    // }}
                                                    // onBlur={() => setFieldValue("firmGst", values.firmGst.toUpperCase())}
                                                    //style={{ textTransform: 'uppercase' }}
                                                    />
                                                </FieldWrappers>
                                                {errors.firmGst && touched.firmGst && (
                                                    <ErrorMessage>{errors.firmGst}</ErrorMessage>
                                                )}
                                            </InputGroup>
                                        </div>
                                    </Wrapper>
                                </ScrollableContent>
                                <ActionSection>
                                    <SideCancelBtn type="button" onClick={handleBackToStep1}>
                                        Back
                                    </SideCancelBtn>
                                    <SubmitBtn type="submit" disabled={addPartnerLoading}>
                                        {addPartnerLoading ? "Submitting..." : "Submit"}
                                    </SubmitBtn>
                                </ActionSection>
                            </Form>
                        )}
                    </Formik>
                )}
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-[#0078BD] py-4 px-4`;
const ErrorMessage = tw.div`text-red-500 text-xs mt-1`;
export default Add;